import {
    selectSet,
    deleteUpload,
    uploadFlight,
    stageFiles,
    setSelectedUpload,
    selectSelectedUpload,
    selectInfraredJobByFlightId,
    selectUploadsForSetSummary,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
} from "./InfraredImageSlice"
import { supportedFileUploadExtensions } from "features/job-management/uploader/constants"
import { routeCsvFiles } from "features/job-management/uploader/mma-telemetry"
import { useProcessing } from "./useInfraredImageProcessing"
import { InfraredUploadFlowWarnings } from "./InfraredImageWarnings"
import { selectHotspotOrthoOverlapIsEnabled } from "features/map/layers/selectors"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import {
    ImagePreviewType,
    JobTypes,
    SampleDataFlightKey,
    UploadComponentProps,
    AddOns,
} from "@nova/constants"
import { get, set } from "nodash"
import {
    deleteInfraredFlight,
    updateInfraredFlight,
} from "features/pubsub/entityActions/flights/infrared"
import {
    selectInfraredImageById,
    selectInfraredImagesAndUploadsWithContextByFlightId,
    selectInfraredFinishedUploadSize,
    selectInfraredUploadSize,
    selectIsInfraredFlightUploading,
    selectSummaryFromInfraredUploads,
} from "features/entity-caching/images/hotspots/selectors"
import { deleteInfraredImage } from "features/pubsub/entityActions/images/infrared"
import {
    CreateUploadFlow,
    CreateUploadFlowProps,
    getGenericUploadFlowRenderComponent,
} from "panels/content/side-panel/upload-flow/UploadFlow"
import { getInfraredImageS3Key } from "panels/content/side-panel/upload-flow/constants"
import { GenericAddFiles } from "panels/content/side-panel/upload-flow/GenericAddFiles"
import { InfraredImagePreviewFiles } from "./InfraredImagePreviewFiles"
import { GenericProcessingFiles } from "panels/content/side-panel/upload-flow/GenericProcessingFiles"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { setHotspotOverlapLayer } from "features/entity-caching/flights/slice"
import {
    CreateImagePreviewModal,
    CreateImagePreviewModalProps,
} from "panels/content/side-panel/upload-flow/preview-files/GenericImagePreviewModal"
import { useAppSelector } from "integrations/redux/hooks"
import { KMLUploadContainer } from "./KMLUploadContainer"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

const getDefaultIrbFilename = (filename: string) => {
    const parts = filename.split(".")
    return parts[0] + "." + parts.at(-1)
}

const fileFilter = (files: File[]) => {
    // IRB + XML pairing (existing behaviour)
    if (files.some((file) => file.name.toLowerCase().endsWith(".irb"))) {
        const irbs = files.filter((file) => file.name.toLowerCase().endsWith(".irb"))
        irbs.forEach((irbFile, irbIdx) => {
            const xmlIndex = files.findIndex(
                (file) =>
                    getDefaultIrbFilename(file.name) ===
                    getDefaultIrbFilename(irbFile.name).slice(0, -4) + ".xml"
            )
            if (xmlIndex !== -1) {
                set(irbFile, "xml", files[xmlIndex])
                files.splice(xmlIndex, 1)
            } else {
                files.splice(irbIdx, 1)
            }
        })
    }
    files = files.filter((file) => !file.name.toLowerCase().endsWith(".xml"))

    // MMA telemetry CSVs are routed through the side-channel so stageFiles can
    // dispatch them with the correct flightId.
    return Promise.resolve(routeCsvFiles(files))
}

const HotspotUploadFlow = CreateUploadFlow({
    selectSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectUploadsForSet: selectInfraredImagesAndUploadsWithContextByFlightId,
    selectUploadsForSetSummary: selectUploadsForSetSummary,
    selectJob: selectInfraredJobByFlightId,
    selectSelectedUpload,
    setSelectedUpload,
    deleteUpload,
    deleteUploadedUpload: deleteInfraredImage,
    stageFiles,
    uploadFlight,
    useProcessing,
    supportedFileUploadExtensions: supportedFileUploadExtensions.infrared,
    fileFilter,
    minUploads: 3,
    deleteFlight: deleteInfraredFlight,
    jobProgressType: JobTypes.Infrared,
    addOnType: AddOns.Hotspots,
    getS3Key: getInfraredImageS3Key,
    selectFinishedUploadSize: selectInfraredFinishedUploadSize,
    selectFlightById: selectInfraredFlightById,
    selectIsUploading: selectIsInfraredFlightUploading,
    selectUploadSize: selectInfraredUploadSize,
    selectUploadStateSummary: selectSummaryFromInfraredUploads,
    updateFlight: updateInfraredFlight,
    setSelectedPlugin: activateSidePanelModal,
    uploadFlowWarnings: InfraredUploadFlowWarnings,
    setOverlapLayer: setHotspotOverlapLayer,
    selectOverlapLayerIsEnabled: selectHotspotOrthoOverlapIsEnabled,
    imagePreviewType: ImagePreviewType.Infrared,
    pluginImageUrl: "/images/plugins/layers/hotspot.svg",
    pluginId: NovaPluginIds.IR,
    sampleDataFlightKey: SampleDataFlightKey.ir2,
} as CreateUploadFlowProps)

const InfraredRenderComponent = getGenericUploadFlowRenderComponent({
    AddFiles: GenericAddFiles,
    PreviewFiles: InfraredImagePreviewFiles,
    ProcessingFiles: GenericProcessingFiles,
})

export const HotspotMultifileUpload = (props: UploadComponentProps) => {
    const job = useAppSelector(selectInfraredJobByFlightId(props.id))
    const flight = useAppSelector(selectInfraredFlightById(props.id))
    const extraFeaturesJSON = get(job, "extraFeatures") as string | undefined
    const skippedProcessing = extraFeaturesJSON?.includes("customHotspotUpload")
    if ((skippedProcessing || flight?.skippedProcessing) && flight) {
        return <KMLUploadContainer {...props} flight={flight} />
    } else {
        return (
            <HotspotUploadFlow {...props}>
                <InfraredRenderComponent />
            </HotspotUploadFlow>
        )
    }
}

export const InfraredImagePreviewModal = CreateImagePreviewModal({
    imagePreviewType: ImagePreviewType.Infrared,
    selectUploadsForSet: selectInfraredImagesAndUploadsWithContextByFlightId,
    deleteUpload: deleteUpload,
    deleteUploadedUpload: deleteInfraredImage,
    selectImageById: selectInfraredImageById,
    selectJobByFlightId: selectInfraredJobByFlightId,
    selectFlightById: selectInfraredFlightById,
} as CreateImagePreviewModalProps)
